import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { TeamClock } from "@/components/TeamClock";
import { PillarsProvider } from "@/contexts/PillarsContext";
import { captureReferralFromUrl } from "@/lib/referral";
import HeaderBar from "@/components/HeaderBar";
import InstallPrompt from "@/components/InstallPrompt";
import LoginPage from "@/pages/LoginPage";
import WelcomePage from "@/pages/WelcomePage";
import LandingPage from "@/pages/LandingPage";
import PrivacyPage, { TermsPage } from "@/pages/PrivacyPage";
import CompliancePage from "@/pages/CompliancePage";
import SchoolsPitchPage from "@/pages/SchoolsPitchPage";
import InstallGuidePage from "@/pages/InstallGuidePage";
import SessionInfoPage from "@/pages/SessionInfoPage";
import SessionPosterPage from "@/pages/SessionPosterPage";
import EventPage from "@/pages/EventPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import CommunityPage from "@/pages/CommunityPage";
import DashboardPage from "@/pages/DashboardPage";
import CoursePlayerPage from "@/pages/CoursePlayerPage";
import CertificatePage from "@/pages/CertificatePage";
import CertificatePrintPage from "@/pages/CertificatePrintPage";
import CatalogPage from "@/pages/CatalogPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import LessonEditorPage from "@/pages/LessonEditorPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import VerifyPage from "@/pages/VerifyPage";
import ImpersonatePage from "@/pages/ImpersonatePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StudentLoginPage from "@/pages/StudentLoginPage";
import InboxPage from "@/pages/InboxPage";
import MyFeedbackPage from "@/pages/MyFeedbackPage";
import RegisterPage from "@/pages/RegisterPage";
import BuyPage from "@/pages/BuyPage";
import PosterPage from "@/pages/PosterPage";
import FrontDeskModePage from "@/pages/FrontDeskModePage";
import ReviewPosterPage from "@/pages/ReviewPosterPage";
import ReviewRedirectPage from "@/pages/ReviewRedirectPage";
import { Toaster } from "@/components/ui/sonner";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import { CoachFab } from "@/components/CoachFab";
import CoachPage from "@/pages/CoachPage";

function ProtectedRoute({ children, allow }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#000000]">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}<CoachFab /></>;
}

function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#000000]">Loading...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function WelcomeRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#000000]">Loading...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <WelcomePage />;
}

function LoginRoute() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#000000]">Loading...</div>;
  if (user) {
    const from = loc.state?.from;
    return <Navigate to={from ? `${from.pathname}${from.search || ""}` : "/dashboard"} replace />;
  }
  return <LoginPage />;
}

function Shell({ children }) {
  const { user } = useAuth();
  return (
    <>
      {user && <HeaderBar />}
      {user && <InstallPrompt />}
      {children}
    </>
  );
}

function AppRoutes() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/privacy-compliance" element={<CompliancePage />} />
        <Route path="/schools" element={<SchoolsPitchPage />} />
        <Route path="/install-guide" element={<InstallGuidePage />} />
        <Route path="/session/:id" element={<SessionInfoPage />} />
        <Route path="/session/:id/poster" element={<SessionPosterPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/events/:slug" element={<EventPage />} />
        <Route path="/welcome" element={<WelcomeRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/student" element={<StudentLoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/buy" element={<BuyPage />} />
        <Route path="/buy/:itemId" element={<BuyPage />} />
        <Route path="/poster" element={<PosterPage />} />
        <Route path="/frontdesk" element={<FrontDeskModePage />} />
        <Route path="/review-poster" element={<ReviewPosterPage />} />
        <Route path="/review" element={<ReviewRedirectPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/coach" element={<ProtectedRoute><CoachPage /></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
        <Route path="/course/:id" element={<ProtectedRoute><CoursePlayerPage /></ProtectedRoute>} />
        <Route path="/certificate/:id" element={<ProtectedRoute><CertificatePrintPage /></ProtectedRoute>} />
        <Route path="/verification/:id" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
        <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
        <Route path="/my-feedback" element={<ProtectedRoute><MyFeedbackPage /></ProtectedRoute>} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allow={["super_admin", "level2_instructor", "level1_instructor", "teacher", "school_admin", "school_board_admin", "team_dev", "team_content"]}>
              <AdminPanelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/lesson/:id"
          element={
            <ProtectedRoute allow={["super_admin", "level2_instructor", "level1_instructor", "team_dev", "team_content"]}>
              <LessonEditorPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <TeamClock />
    </Shell>
  );
}

export default function App() {
  useEffect(() => { captureReferralFromUrl(); }, []);
  return (
    <div className="app-shell" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
          <PillarsProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
            <UpdatePrompt />
          </PillarsProvider>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
